import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, of } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { NotificationsApiActions, NotificationsUiActions } from '../actions';
import { showNotificationPreferencesUpdatedModal } from '../../constants/notification-preferences-updated-modal';

@Injectable()
export class NotificationsEffects {
  private actions$ = inject(Actions);
  private notificationService = inject(NotificationService);
  private toastService = inject(ToastService);

  getNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUiActions.GetNotifications),
      mergeMap(({ params }) =>
        this.notificationService.getNotifications(params).pipe(
          map((response) => NotificationsApiActions.GetNotificationsSuccess({ payload: response })),
          catchError((error) => {
            this.toastService.show(`Error fetching notifications: ${error.message || error}`, 'error');
            return of(NotificationsApiActions.GetNotificationsFailure({ errors: error }));
          })
        )
      )
    )
  );

  getNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUiActions.GetNotificationPreferences),
      mergeMap(() =>
        this.notificationService.getNotificationPreferences().pipe(
          map((response) => NotificationsApiActions.GetNotificationPreferencesSuccess({ payload: response })),
          catchError((error) => {
            this.toastService.show(`Error fetching notification preferences: ${error.message || error}`, 'error');
            return of(NotificationsApiActions.GetNotificationPreferencesFailure({ errors: error }));
          })
        )
      )
    )
  );

  markNotificationsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUiActions.MarkNotificationsRead),
      // Sequential, so an older response cannot overwrite the unread count from a newer one.
      concatMap(({ ids }) =>
        this.notificationService.markRead({ ids }).pipe(
          map((response) => NotificationsApiActions.MarkNotificationsReadSuccess({ ids, count: response.count ?? 0 })),
          catchError((error) => of(NotificationsApiActions.MarkNotificationsReadFailure({ errors: error })))
        )
      )
    )
  );

  updateNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUiActions.UpdateNotificationPreferences),
      mergeMap(({ preferences }) =>
        this.notificationService.updateNotificationPreferences(preferences).pipe(
          map((response) => {
            // Handle partial success with errors
            if (!Array.isArray(response) && response.errors && response.errors.length > 0) {
              this.toastService.show('Some preferences failed to update', 'warning');
            } else {
              showNotificationPreferencesUpdatedModal();
            }
            return NotificationsApiActions.UpdateNotificationPreferencesSuccess({ payload: response });
          }),
          catchError((error) => {
            this.toastService.show(`Error updating notification preferences: ${error.message || error}`, 'error');
            return of(NotificationsApiActions.UpdateNotificationPreferencesFailure({ errors: error }));
          })
        )
      )
    )
  );
}
